// =============================================================================
// family.ts — SIL-844 family-grain derivations
// =============================================================================
//
// Pure, DB-free functions behind GET /api/skus/families and the ops
// `/skus` family view. Kept in @siliconx/shared (not apps/api or
// apps/operations) because the plan's own words are the reason: "The API,
// the UI and any future backfill must read the same list" — SIL-846's
// backfill disagreeing with FLAT_ASSET_CLASSES does real damage (mis-parents
// a flat SKU, or leaves a genuinely-configurable SKU unparented forever).
// =============================================================================

import type { SkuVerdict } from '../dto/sku.js';

// -----------------------------------------------------------------------------
// Flat classes — one named constant (SIL-844 decision, trap #2)
// -----------------------------------------------------------------------------

/**
 * Asset classes with no configuration dimension: each SKU under one of these
 * classes correctly stands alone (no master, ever) — it IS one
 * configuration. Every other class expects a family.
 */
export const FLAT_ASSET_CLASSES = ['cpu', 'memory', 'cloud_vm', 'ib_switch', 'storage'] as const;
export type FlatAssetClassId = (typeof FLAT_ASSET_CLASSES)[number];

const FLAT_ASSET_CLASS_SET: ReadonlySet<string> = new Set(FLAT_ASSET_CLASSES);

/** True unless `assetClassId` is one of the five FLAT_ASSET_CLASSES. */
export function expectsFamily(assetClassId: string): boolean {
  return !FLAT_ASSET_CLASS_SET.has(assetClassId);
}

// -----------------------------------------------------------------------------
// Product-line grouping (SIL-844 decision 1)
// -----------------------------------------------------------------------------

/**
 * Product-line grouping key for a `verified_sku`: everything from the FIRST
 * `_GEN` token onward is the generation dimension, not part of the line
 * identity.
 *
 * - `HPE_PROLIANT_DL380_GEN10_PLUS_V2` -> line `HPE_PROLIANT_DL380`, keeping
 *   the full `GEN10_PLUS_V2` generation token distinct from a sibling
 *   `..._GEN10` row — a naive suffix strip (e.g. regex `/_GEN\d+$/`)
 *   collapses both to `GEN10` and loses that distinction.
 * - `HPE_PROLIANT_DL380A_GEN11` -> line `HPE_PROLIANT_DL380A`, never
 *   `HPE_PROLIANT_DL380` — the trailing `A` is a different product, not a
 *   truncation artifact. Splitting on the literal substring `_GEN` (not a
 *   token-boundary regex) is what keeps this correct: `DL380A` never gets
 *   mistaken for a `DL380` + generation suffix.
 * - No `_GEN` token at all -> the whole string is the line (a
 *   single-generation product; callers label this "base model", not by
 *   asset class).
 */
export function lineKey(verifiedSku: string): string {
  const idx = verifiedSku.indexOf('_GEN');
  return idx === -1 ? verifiedSku : verifiedSku.slice(0, idx);
}

/**
 * The generation token itself — everything from `_GEN` onward, leading
 * underscore stripped — or null if `verifiedSku` carries none (a
 * single-generation line).
 */
export function generationToken(verifiedSku: string): string | null {
  const idx = verifiedSku.indexOf('_GEN');
  return idx === -1 ? null : verifiedSku.slice(idx + 1);
}

// -----------------------------------------------------------------------------
// Spread + verdict (SIL-844 decisions 2 + 3)
// -----------------------------------------------------------------------------

export const SPREAD_DEFECT_THRESHOLD = 10;
export const SPREAD_ATTENTION_THRESHOLD = 5;

/**
 * Spread = max variant price / min variant price. NULL when fewer than two
 * variants carry a price — a ratio needs two points to compare, and a
 * single priced variant is not evidence of anything.
 */
export function computeSpread(variantPrices: ReadonlyArray<number | null | undefined>): number | null {
  const priced = variantPrices.filter(
    (p): p is number => typeof p === 'number' && Number.isFinite(p) && p > 0,
  );
  if (priced.length < 2) return null;
  return Math.max(...priced) / Math.min(...priced);
}

/**
 * Family verdict, first-match-wins (SIL-844 decision 3):
 *   no variants -> spread>=10x -> spread>=5x -> no band -> no config stated
 *   -> single variant -> coherent.
 */
export function computeVerdict(input: {
  variantCount: number;
  spread: number | null;
  hasBand: boolean;
  allVariantsNoConfig: boolean;
}): SkuVerdict {
  if (input.variantCount === 0) return 'no_variants';
  if (input.spread !== null && input.spread >= SPREAD_DEFECT_THRESHOLD) return 'incoherent_defect';
  if (input.spread !== null && input.spread >= SPREAD_ATTENTION_THRESHOLD) return 'incoherent_attention';
  if (!input.hasBand) return 'no_band';
  if (input.allVariantsNoConfig) return 'no_config_stated';
  if (input.variantCount === 1) return 'single_variant';
  return 'coherent';
}

// -----------------------------------------------------------------------------
// Row-level flags shared by the family variant list and the standalone list
// -----------------------------------------------------------------------------

/**
 * `class_attributes = '{}'` presence check (SIL-844 trap: `class_attributes`
 * mixes identity, attributes, configuration and pipeline state — never
 * render it as "the spec", only as a presence/absence signal).
 */
export function hasNoConfigStated(classAttributes: unknown): boolean {
  if (classAttributes == null) return true;
  if (typeof classAttributes !== 'object') return false;
  return Object.keys(classAttributes as Record<string, unknown>).length === 0;
}

/**
 * "Identity is a scraped listing title" (SIL-844 exception section 6): true
 * when `verified_sku` contains a space or a comma — the seller's raw
 * listing text used as a model identity instead of a canonicalized token
 * string (e.g. `CISCO UCSB-B200-M6_2X6330CPU_384GB UCS B200 M6 BLADE
 * SERVER, 2X6330, 3`).
 *
 * This is NOT `isGenPlaceholderSku` (naming.ts) — confirmed against prod
 * data (462 sampled rows) that the two anomaly classes are disjoint: 18
 * `GEN-` placeholder rows (e.g. `GEN-01A31BC0`, a generated stand-in
 * identity) vs. 16 space/comma rows (raw listing text), zero overlap. A
 * `GEN-` placeholder is a real anomaly, just a different one — it is not
 * this predicate's job to catch it.
 */
export function isScrapedListingIdentity(verifiedSku: string | null | undefined): boolean {
  if (verifiedSku == null) return false;
  return verifiedSku.includes(' ') || verifiedSku.includes(',');
}
